import { BaseActivityEvent } from './BaseActivityEvent'
import { ReactComponent as Icon } from './icons/aiEvent.svg'
import { Actor } from './Actor'
import { ActivityEventPosition, ArticleAIGeneratedEvent, BaseActivityEventProps } from './Types'
import { EventWizardButton, NonChannelWizard } from 'components/DocRightRail/screens/AIAssistant/EventWizardButton'
import Styles from './ArticleAiGenerated.module.scss'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { eventPromptText } from 'components/ActivityEvent/utils'
import { IconButton } from 'components/IconButton/IconButton'
import { Copy } from 'almanac-ui/icons'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { EventIconContainer } from 'components/ActivityEvent/EventIconContainer'
import { ChannelIcon } from 'components/ChannelIcon/ChannelIcon'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

interface Props extends Partial<BaseActivityEventProps>, ActivityEventPosition {
  event: ArticleAIGeneratedEvent
  isTruncated?: boolean
  openWizard?: boolean
}

export enum GenerateActions {
  Generic = 'generic',
  AddMore = 'add_more',
  TryAgain = 'retry',
  AskBlaze = 'ask',
  AdjustPrompt = 'adjust_prompt',
  MakeLonger = 'make_longer',
  MakeShorter = 'make_shorter',
}

export const generateActionEvents: Record<GenerateActions, string> = {
  [GenerateActions.Generic]: 'generated this doc',
  [GenerateActions.AddMore]: 'brainstormed more ideas',
  [GenerateActions.TryAgain]: 'asked Blaze to retry the last prompt',
  [GenerateActions.AskBlaze]: 'asked Blaze',
  [GenerateActions.AdjustPrompt]: 'revised the generation',
  [GenerateActions.MakeLonger]: 'asked Blaze to lengthen this doc',
  [GenerateActions.MakeShorter]: 'asked Blaze to shorten this doc',
}

export const generationTypeEvents: Partial<Record<GenerationType, string>> = {
  [GenerationType.InlineGeneric]: 'updated this doc with this prompt',
}

interface CopyButtonProps {
  prompt: string
}

const CopyPromptIconButton = ({ prompt }: CopyButtonProps) => {
  const handlePress = async () => {
    await navigator.clipboard.writeText(prompt)
    showToastMessage('Prompt copied', 'success')
  }

  return <IconButton aria-label="Copy prompt" size="md" icon={Copy} onPress={handlePress} />
}

const ArticleAIGenerated = ({ event, actionsEnabled, isTruncated, openWizard = true, ...props }: Props) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const { user, data } = event
  const actionType = data.action as GenerateActions

  const isWizardEvent = [...Object.values(ChannelType), ...Object.values(NonChannelWizard)].includes(
    event.data.generation_type as ChannelType,
  )

  const promptText = eventPromptText(event)

  return (
    <BaseActivityEvent
      {...props}
      icon={
        <EventIconContainer>
          <ChannelIcon defaultIcon={Icon} channel={event.data.generation_type as ChannelType} />
        </EventIconContainer>
      }
      timestamp={event.timestamp}
      content={
        <>
          <Actor displayName={user.display_name || 'Anonymous User'} userId={user?.id} />
          {` ${
            generateActionEvents[actionType] ||
            generationTypeEvents[data.generation_type as GenerationType] ||
            generateActionEvents[GenerateActions.Generic]
          }`}
        </>
      }
      message={promptText && <p className={isTruncated ? Styles.truncated : ''}>{promptText}</p>}
      actions={
        <div className={Styles.actions}>
          {promptText && <CopyPromptIconButton prompt={promptText} />}
          {isWizardEvent && !isSystemRedesignEnabled && <EventWizardButton event={event} variant="tertiary" />}
        </div>
      }
      actionsEnabled={openWizard && (isWizardEvent || !!promptText)}
    />
  )
}

export { ArticleAIGenerated }
